import React, { useState, useEffect } from "react";
import axios from "axios";
import AdminLayout from "@/components/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, X, Check } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PricingManagement = () => {
  const [pricingRules, setPricingRules] = useState([]);
  const [newRule, setNewRule] = useState({
    start_date: "",
    end_date: "",
    price_per_night: "",
    minimum_stay: "",
    notes: "",
  });
  const [loading, setLoading] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [editForm, setEditForm] = useState({
    start_date: "",
    end_date: "",
    price_per_night: "",
    minimum_stay: "",
    notes: "",
  });

  useEffect(() => {
    fetchPricingRules();
  }, []);

  const fetchPricingRules = async () => {
    try {
      const response = await axios.get(`${API}/pricing`);
      setPricingRules(response.data);
    } catch (error) {
      console.error("Error fetching pricing rules:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/pricing`, {
        ...newRule,
        price_per_night: parseFloat(newRule.price_per_night),
        minimum_stay: parseInt(newRule.minimum_stay),
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Verðregla bætt við!");
      setNewRule({
        start_date: "",
        end_date: "",
        price_per_night: "",
        minimum_stay: "",
        notes: "",
      });
      fetchPricingRules();
    } catch (error) {
      toast.error("Villa við að bæta við verðreglu");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (rule) => {
    setEditingRule(rule.id);
    setEditForm({
      start_date: rule.start_date,
      end_date: rule.end_date,
      price_per_night: rule.price_per_night.toString(),
      minimum_stay: rule.minimum_stay.toString(),
      notes: rule.notes || "",
    });
  };

  const handleCancelEdit = () => {
    setEditingRule(null);
    setEditForm({
      start_date: "",
      end_date: "",
      price_per_night: "",
      minimum_stay: "",
      notes: "",
    });
  };

  const handleSaveEdit = async (ruleId) => {
    try {
      const token = localStorage.getItem("token");
      await axios.put(`${API}/pricing/${ruleId}`, {
        start_date: editForm.start_date,
        end_date: editForm.end_date,
        price_per_night: parseFloat(editForm.price_per_night),
        minimum_stay: parseInt(editForm.minimum_stay),
        notes: editForm.notes,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Verðregla uppfærð!");
      setEditingRule(null);
      fetchPricingRules();
    } catch (error) {
      toast.error("Villa við að uppfæra verðreglu");
    }
  };

  const handleDelete = async (ruleId) => {
    if (!window.confirm("Ertu viss um að þú viljir eyða þessari verðreglu?")) return;
    
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/pricing/${ruleId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Verðreglu eytt");
      fetchPricingRules();
    } catch (error) {
      toast.error("Villa við að eyða verðreglu");
    }
  };

  return (
    <AdminLayout>
      <div>
        <h1 className="text-3xl md:text-4xl font-display font-bold tracking-tight mb-8" data-testid="pricing-title">
          Verðstjórnun
        </h1>

        {/* Add New Pricing Rule */}
        <div className="bg-white border border-neutral-200 rounded-lg p-6 md:p-8 mb-8">
          <h2 className="text-xl font-heading font-semibold mb-6">Bæta við verðreglu</h2>
          <form onSubmit={handleSubmit} data-testid="pricing-form">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <div>
                <Label htmlFor="start-date" className="text-sm font-ui font-medium text-neutral-700 mb-2 block">
                  Upphafsdagur
                </Label>
                <Input
                  id="start-date"
                  type="date"
                  value={newRule.start_date}
                  onChange={(e) => setNewRule({ ...newRule, start_date: e.target.value })}
                  required
                  className="px-4 py-2 rounded-lg"
                  data-testid="form-input-startdate"
                />
              </div>

              <div>
                <Label htmlFor="end-date" className="text-sm font-ui font-medium text-neutral-700 mb-2 block">
                  Lokadagur
                </Label>
                <Input
                  id="end-date"
                  type="date"
                  value={newRule.end_date}
                  onChange={(e) => setNewRule({ ...newRule, end_date: e.target.value })}
                  required
                  min={newRule.start_date}
                  className="px-4 py-2 rounded-lg"
                  data-testid="form-input-enddate"
                />
              </div>

              <div>
                <Label htmlFor="price" className="text-sm font-ui font-medium text-neutral-700 mb-2 block">
                  Verð á nótt (€)
                </Label>
                <Input
                  id="price"
                  type="number"
                  step="0.01"
                  min="0"
                  value={newRule.price_per_night}
                  onChange={(e) => setNewRule({ ...newRule, price_per_night: e.target.value })}
                  required
                  className="px-4 py-2 rounded-lg"
                  data-testid="form-input-price"
                />
              </div>

              <div>
                <Label htmlFor="min-stay" className="text-sm font-ui font-medium text-neutral-700 mb-2 block">
                  Lágmarksdvöl (nætur)
                </Label>
                <Input
                  id="min-stay"
                  type="number"
                  min="1"
                  value={newRule.minimum_stay}
                  onChange={(e) => setNewRule({ ...newRule, minimum_stay: e.target.value })}
                  required
                  className="px-4 py-2 rounded-lg"
                  data-testid="form-input-minstay"
                />
              </div>
            </div>

            <div className="mb-6">
              <Label htmlFor="notes" className="text-sm font-ui font-medium text-neutral-700 mb-2 block">
                Athugasemdir (valfrjálst)
              </Label>
              <Input
                id="notes"
                value={newRule.notes}
                onChange={(e) => setNewRule({ ...newRule, notes: e.target.value })}
                placeholder="T.d. Sumartímabil, Háannatími, osfrv."
                className="px-4 py-2 rounded-lg"
                data-testid="form-input-notes"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="bg-primary hover:bg-primary-600"
              data-testid="add-pricing-button"
            >
              <Plus className="w-4 h-4 mr-2" />
              {loading ? "Bæti við..." : "Bæta við verðreglu"}
            </Button>
          </form>
        </div>

        {/* Existing Pricing Rules */}
        <div className="bg-white border border-neutral-200 rounded-lg p-6 md:p-8">
          <h2 className="text-xl font-heading font-semibold mb-6">Núverandi verðreglur</h2>
          {pricingRules.length === 0 ? (
            <p className="text-muted-foreground font-body text-center py-8">Engar verðreglur. Bættu við fyrstu reglunni hér að ofan.</p>
          ) : (
            <div className="space-y-4">
              {pricingRules.map((rule) => (
                <div
                  key={rule.id}
                  className="border border-neutral-200 rounded-lg p-4 md:p-6 hover:border-primary-200 transition-colors duration-200"
                  data-testid={`pricing-rule-${rule.id}`}
                >
                  {editingRule === rule.id ? (
                    // Edit mode
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        <div>
                          <Label className="text-xs text-muted-foreground mb-1 block">Upphafsdagur</Label>
                          <Input
                            type="date"
                            value={editForm.start_date}
                            onChange={(e) => setEditForm({ ...editForm, start_date: e.target.value })}
                            className="h-9"
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-muted-foreground mb-1 block">Lokadagur</Label>
                          <Input
                            type="date"
                            value={editForm.end_date}
                            onChange={(e) => setEditForm({ ...editForm, end_date: e.target.value })}
                            min={editForm.start_date}
                            className="h-9"
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-muted-foreground mb-1 block">Verð á nótt (€)</Label>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            value={editForm.price_per_night}
                            onChange={(e) => setEditForm({ ...editForm, price_per_night: e.target.value })}
                            className="h-9"
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-muted-foreground mb-1 block">Lágmarksdvöl</Label>
                          <Input
                            type="number"
                            min="1"
                            value={editForm.minimum_stay}
                            onChange={(e) => setEditForm({ ...editForm, minimum_stay: e.target.value })}
                            className="h-9"
                          />
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground mb-1 block">Athugasemdir</Label>
                        <Input
                          value={editForm.notes}
                          onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                          placeholder="Athugasemdir..."
                          className="h-9"
                        />
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={() => handleSaveEdit(rule.id)}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          <Check className="w-4 h-4 mr-1" />
                          Vista
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={handleCancelEdit}
                        >
                          <X className="w-4 h-4 mr-1" />
                          Hætta við
                        </Button>
                      </div>
                    </div>
                  ) : (
                    // View mode
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-2">
                          <div>
                            <span className="text-xs font-ui font-medium text-muted-foreground uppercase tracking-wider block mb-1">
                              Tímabil
                            </span>
                            <span className="font-body text-sm">
                              {rule.start_date} → {rule.end_date}
                            </span>
                          </div>
                          <div>
                            <span className="text-xs font-ui font-medium text-muted-foreground uppercase tracking-wider block mb-1">
                              Verð/nótt
                            </span>
                            <span className="font-body font-semibold text-primary-600 text-lg">€{rule.price_per_night}</span>
                          </div>
                          <div>
                            <span className="text-xs font-ui font-medium text-muted-foreground uppercase tracking-wider block mb-1">
                              Lágmarksdvöl
                            </span>
                            <span className="font-body">{rule.minimum_stay} nætur</span>
                          </div>
                          {rule.notes && (
                            <div>
                              <span className="text-xs font-ui font-medium text-muted-foreground uppercase tracking-wider block mb-1">
                                Athugasemd
                              </span>
                              <span className="font-body text-sm">{rule.notes}</span>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleEdit(rule)}
                          className="text-blue-600 hover:bg-blue-50 hover:text-blue-700"
                          data-testid={`edit-pricing-${rule.id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(rule.id)}
                          className="text-red-600 hover:bg-red-50 hover:text-red-700"
                          data-testid={`delete-pricing-${rule.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default PricingManagement;
